/**
 * @file: CommentRow.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * One internal-discussion comment: an avatar (initials fallback) + author +
 * relative time on top, the body text, and an attachment grid. The author may
 * delete their own comment via a trailing ✕ (confirmed).
 */

import { Typography } from '@euler/app/components/typography';
import { MediaAttachmentGrid } from '@euler/app/flows/leads/pages/detail/components/MediaAttachmentGrid';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { Avatar } from '@euler/components/Avatar';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadMessageView } from '@euler/model/lead/types';
import { dateFromValue } from '@euler/utils/datetime';
import { X } from 'lucide-react-native';
import { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

// A compact "x分钟前 / x小时前 / x天前 / MM-dd" stamp; the thread reads
// top-to-bottom so a relative time is friendlier than a full timestamp.
const relativeTime = (value: string | Date): string => {
  let then = 0;
  try {
    then = dateFromValue(value).getTime();
  } catch {
    return '';
  }
  const diff = Date.now() - then;
  if (diff < 60_000) return '刚刚';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}小时前`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}天前`;
  const d = new Date(then);
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
};

export const CommentRow = memo(
  ({
    message,
    canDelete,
    first,
    onDelete,
  }: {
    message: LeadMessageView;
    canDelete: boolean;
    // The first comment omits the top divider so the card has no leading line.
    first?: boolean;
    onDelete: (id: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const avatarSize = rpx(30);
    return (
      <View
        style={{
          flexDirection: 'row',
          paddingVertical: rpx(12),
          borderTopWidth: first ? 0 : 1,
          borderTopColor: LD.hairline,
        }}
      >
        <Avatar
          name={message.author.name || '?'}
          uri={message.author.avatar ?? undefined}
          bold
          textStyle={{ fontSize: rpx(14), lineHeight: rpx(18) }}
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: avatarSize / 2,
            marginRight: rpx(10),
          }}
        />
        <View style={{ flex: 1 }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Typography.Text
              size={rpx(13)}
              color={LD.ink}
              medium
              style={{ flex: 1 }}
              numberOfLines={1}
            >
              {message.author.name || '—'}
            </Typography.Text>
            <Typography.Text size={rpx(11)} color={LD.mist}>
              {relativeTime(message.createdAt)}
            </Typography.Text>
            {canDelete ? (
              <TouchableOpacity
                activeOpacity={0.7}
                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                onPress={() => onDelete(message.id)}
                style={{ marginLeft: rpx(8) }}
              >
                <X color={LD.mist} size={rpx(14)} />
              </TouchableOpacity>
            ) : null}
          </View>

          {message.body ? (
            <Typography.Text
              size={rpx(14)}
              color={LD.steel}
              mt={rpx(4)}
              lineHeight={rpx(20)}
            >
              {message.body}
            </Typography.Text>
          ) : null}

          {message.attachments?.length ? (
            <MediaAttachmentGrid attachments={message.attachments} />
          ) : null}
        </View>
      </View>
    );
  },
);
